import { PropsWithChildren } from 'react';
import { useMutation } from '@tanstack/react-query';
import * as AlertDialog from '@givebutter/primitives/alert-dialog';
import { useToast } from '@givebutter/primitives/toast';
import { deleteWorkflow } from 'api/workflows';
import { useCurrentAccount } from 'customHooks/useCurrentAccount';
import { queryClient } from 'queries/queryClient';
import { workflowQueryKeys } from 'queries/workflow-query-keys';
import { GivebutterAxiosError } from 'types/error';

interface UseDeleteWorkflow {
    onSuccess?: () => void;
    shouldRedirect: boolean;
}
const useDeleteWorkflow = ({ onSuccess, shouldRedirect }: UseDeleteWorkflow) => {
    const { accountId } = useCurrentAccount();
    const { success, error } = useToast();

    return useMutation<unknown, GivebutterAxiosError, { id: number }>({
        mutationFn: ({ id }) => deleteWorkflow(accountId, id),
        onSuccess: () => {
            success('Workflow successfully deleted!');
            queryClient.invalidateQueries({
                queryKey: workflowQueryKeys.workflows(accountId),
                refetchType: shouldRedirect ? 'none' : undefined,
            });
            onSuccess?.();
        },
        onError: e => {
            error(e.response?.data.message || 'Unable to delete workflow. Please try again.');
        },
    });
};

interface DeleteWorkflowAlertDialogProps extends PropsWithChildren {
    workflowId: number;
    onSuccess?: UseDeleteWorkflow['onSuccess'];
    shouldRedirect?: boolean;
}
const DeleteWorkflowAlertDialog = ({
    children,
    workflowId,
    onSuccess,
    shouldRedirect = false,
}: DeleteWorkflowAlertDialogProps) => {
    const { mutate: submitDelete, isPending: isDeletingWorkflow } = useDeleteWorkflow({
        onSuccess,
        shouldRedirect,
    });

    return (
        <AlertDialog.Root type="error">
            {children}
            <AlertDialog.Portal>
                <AlertDialog.Overlay />
                <AlertDialog.Content>
                    <AlertDialog.Header>
                        <AlertDialog.Icon />
                        <AlertDialog.Close />
                    </AlertDialog.Header>
                    <AlertDialog.Body>
                        <AlertDialog.Title>Delete workflow?</AlertDialog.Title>
                        <AlertDialog.Description>
                            You will not be able to undo this action. Are you sure?
                        </AlertDialog.Description>
                    </AlertDialog.Body>
                    <AlertDialog.Footer>
                        <AlertDialog.Cancel>Cancel</AlertDialog.Cancel>
                        <AlertDialog.Action
                            onClick={() => submitDelete({ id: workflowId })}
                            disabled={isDeletingWorkflow}
                        >
                            Delete
                        </AlertDialog.Action>
                    </AlertDialog.Footer>
                </AlertDialog.Content>
            </AlertDialog.Portal>
        </AlertDialog.Root>
    );
};

const DeleteWorkflowAlertDialogTrigger = AlertDialog.Trigger;
const Root = DeleteWorkflowAlertDialog;
const Trigger = DeleteWorkflowAlertDialogTrigger;

export {
    DeleteWorkflowAlertDialog,
    DeleteWorkflowAlertDialogTrigger,
    //
    Root,
    Trigger,
    //
    useDeleteWorkflow,
};

export type { DeleteWorkflowAlertDialogProps, UseDeleteWorkflow };
